import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import useComponentSize from "@rehooks/component-size";
import { FixedSizeList as List } from "react-window";
import JSZIP, { loadAsync } from "jszip";
import axios from "axios";
import { Helmet } from "react-helmet";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import {
  Warning,
  Storage,
  FileCopy,
  AspectRatio,
  Collections,
  Close,
} from "@material-ui/icons";
import UiButton from "../../common/UiButton";
import UiFileDropzone from "../../common/UiFileDropzone";
import UiInput from "../../common/UiInput";
import UiProgress from "../../common/UiProgess";
import UiFormTitle from "../../common/UiFormTitle";
import humanFileSize from "../../../tools/humanFileSize";
import filenameToMimeType from "../../../tools/filenameToMimeType";
import findDuplicateFileNames from "../../../tools/findDuplicateFileNames";
import findBigFiles from "../../../tools/findBigFiles";
import {
  getUploadSizeOverageBytes,
  parseUploadSizeToBytes,
} from "../../../tools/parseUploadSize";
import findFilesWithDifferentDimensions from "../../../tools/findFilesWithDifferentDimensions";
import createImageBitmapFromFile from "../../../tools/createImageBitmapFromFile";
import {
  inspectZipEntries,
  checkZipConformity,
  buildZipConformityMessage,
} from "../../../tools/inspectZip";
import makeUniqueZipName from "../../../tools/makeUniqueZipName";
import ImportFileListRow, {
  FILE_LIST_ROW_HEIGHT,
  FileListOuterElement,
  ImportFileListItemData,
} from "./ImportFileListRow";
import ImportProjectSummary from "./ImportProjectSummary";
import ImportIssueChip from "./ImportIssueChip";
import {
  IMPORT_ALLOWED_FILE_TYPES_ACCEPT,
  IMPORT_ALLOWED_FILE_TYPES_LABEL,
} from "../allowedFileTypes";

const yieldToUi = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });

const FILE_LOAD_YIELD_EVERY = 50;

const FILE_LIST_MIN_HEIGHT = 240;

/** Max decode width for the preview modal (display is CSS-scaled). */
const PREVIEW_MAX_WIDTH = 1200;

/** When true, duplicate file names in the zip get suffixed (_2, _3, …) instead of overwriting. */
const RENAME_DUPLICATE_FILES_IN_ZIP = true;

type ImportListFile = {
  uid: number;
  file: File;
};

// Fold streamed zip chunks into the disk-backed Blob every ~8MB so the JS heap
// only ever holds a small batch instead of the whole (potentially GB) archive.
const ZIP_STREAM_FLUSH_BYTES = 8 * 1024 * 1024;

const IMAGE_FILE_EXTENSIONS = [".jpg", ".jpeg", ".png"];

const isImageFileName = (name: string): boolean =>
  IMAGE_FILE_EXTENSIONS.some((ext) => name.toLowerCase().endsWith(ext));

// Keep only files sitting at the top level of a dropped folder (or standalone
// selections). react-dropzone exposes a `path` like "folder/img.jpg" for a
// top-level file and "folder/sub/img.jpg" for a nested one; standalone files
// have no folder segment. Anything deeper is silently ignored.
const isTopLevelFile = (file: File): boolean => {
  const path = (file as any).path as string | undefined;
  if (!path) {
    return true;
  }
  const segments = path.replace(/^\/+/, "").split("/");
  return segments.length <= 2;
};

const toRawFiles = (entries: ImportListFile[]) => entries.map((entry) => entry.file);

const ImportLocalForm = (props: any) => {
  const {
    classes,
    history,
    uploadFiles,
    project,
    progress,
    setProgress,
    isUploading,
    uploadProperties,
    cancelUpload,
  } = props;

  const [datas, setDatas] = useState<any>({});
  const [dropError, setDropError] = useState<string>("");
  const [uploadError, setUploadError] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [isPreparingZip, setIsPreparingZip] = useState(false);
  const [listFiles, setListFiles] = useState<ImportListFile[]>([]);
  const nextFileUidRef = useRef(0);
  const createListFile = useCallback((file: File): ImportListFile => {
    const entry = { uid: nextFileUidRef.current, file };
    nextFileUidRef.current += 1;
    return entry;
  }, []);
  const [bigFiles, setBigFiles] = useState<string[]>([]);
  const [duplicateFileNames, setDuplicateFileNames] = useState<string[]>([]);
  const [
    filesWithDifferentDimensions,
    setFilesWithDifferentDimensions,
  ] = useState<string[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [isValidatingFiles, setIsValidatingFiles] = useState(false);
  const [loadProgress, setLoadProgress] = useState<{
    loaded: number;
    total: number;
  }>({ loaded: 0, total: 0 });
  // When a single, already-clean zip is dropped (flat, images only, no
  // __MACOSX), we keep the original file to upload it as-is instead of
  // re-zipping the extracted images.
  const [passthroughZip, setPassthroughZip] = useState<File | null>(null);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  const totalSize = useMemo(
    () => listFiles.reduce((sum, entry) => sum + entry.file.size, 0),
    [listFiles],
  );
  const maxRequestSizeLabel =
    uploadProperties?.["max-request-size"] ?? "1G";
  const maxRequestBytes = useMemo(
    () => parseUploadSizeToBytes(maxRequestSizeLabel) ?? 0,
    [maxRequestSizeLabel],
  );
  const totalSizeOverBytes = useMemo(
    () => getUploadSizeOverageBytes(totalSize, maxRequestSizeLabel),
    [totalSize, maxRequestSizeLabel],
  );
  const moreThanMaxTotalSize = totalSizeOverBytes > 0;
  const bigSet = useMemo(() => new Set(bigFiles), [bigFiles]);
  const duplicateSet = useMemo(
    () => new Set(duplicateFileNames),
    [duplicateFileNames],
  );
  const diffDimSet = useMemo(
    () => new Set(filesWithDifferentDimensions),
    [filesWithDifferentDimensions],
  );

  // Remove every file flagged as too big, duplicate, or wrong dimensions.
  const shouldKeepFileOnErrorCleanup = (file: File) => {
    const name = file.name;
    return (
      !bigSet.has(name) && !duplicateSet.has(name) && !diffDimSet.has(name)
    );
  };

  type IssueType = "big" | "duplicates" | "dimensions";

  const shouldKeepFileOnTypeCleanup = (file: File, type: IssueType) => {
    const name = file.name;
    if (type === "big") {
      return !bigSet.has(name);
    }
    if (type === "dimensions") {
      return !diffDimSet.has(name);
    }
    return !duplicateSet.has(name);
  };

  const countRemovableByType = (type: IssueType) => {
    if (type === "duplicates") {
      return listFiles.filter((entry) =>
        duplicateSet.has(entry.file.name),
      ).length;
    }
    if (type === "big") {
      return listFiles.filter((entry) => bigSet.has(entry.file.name)).length;
    }
    return listFiles.filter((entry) => diffDimSet.has(entry.file.name)).length;
  };

  const matchesFilter = (name: string, filter: string | null) => {
    if (!filter) return true;
    if (filter === "big") return bigSet.has(name);
    if (filter === "duplicates") return duplicateSet.has(name);
    if (filter === "dimensions") return diffDimSet.has(name);
    return true;
  };

  const filesWithIssuesCount = useMemo(
    () =>
      listFiles.filter((entry) => !shouldKeepFileOnErrorCleanup(entry.file))
        .length,
    [listFiles, bigSet, duplicateSet, diffDimSet],
  );

  const fileListScrollRef = useRef<HTMLDivElement>(null);
  const fileListSize = useComponentSize(fileListScrollRef);
  const fileListHeight = Math.max(fileListSize.height, FILE_LIST_MIN_HEIGHT);

  const displayedFiles = useMemo(() => {
    const entries = listFiles.filter((entry) =>
      matchesFilter(entry.file.name, activeFilter),
    );

    if (activeFilter !== "duplicates") {
      return entries;
    }

    entries.sort(
      (a, b) =>
        a.file.name.localeCompare(b.file.name) ||
        a.uid - b.uid,
    );

    const countByName = new Map<string, number>();
    entries.forEach(({ file }) =>
      countByName.set(file.name, (countByName.get(file.name) ?? 0) + 1),
    );

    const indexByName = new Map<string, number>();
    return entries.map((entry) => {
      const copyIndex = (indexByName.get(entry.file.name) ?? 0) + 1;
      indexByName.set(entry.file.name, copyIndex);
      return {
        ...entry,
        copyIndex,
        copyTotal: countByName.get(entry.file.name)!,
      };
    });
  }, [listFiles, activeFilter, bigSet, duplicateSet, diffDimSet]);

  const onDrop = async (acceptedFiles: File[]) => {
    setDropError("");
    setIsLoadingFiles(true);
    setPassthroughZip(null);
    setLoadProgress({ loaded: 0, total: acceptedFiles.length });
    await yieldToUi();

    const listFileNames = listFiles.map((entry) => entry.file.name);
    const zipFiles = acceptedFiles.filter(
      (file: File) =>
        !listFileNames.includes(file.name) &&
        (file.name.endsWith(".zip") ||
          file.type === "application/zip" ||
          file.type === "application/x-zip-compressed" ||
          file.type === "application/octet-stream"),
    );

    // Loose files: keep only top-level images. Subfolders, system files and any
    // non-image (.txt, .DS_Store, files without extension…) are ignored silently.
    const looseImageFiles = acceptedFiles.filter(
      (file: File) =>
        !file.name.endsWith(".zip") &&
        isImageFileName(file.name) &&
        isTopLevelFile(file),
    );

    const isImageEntryName = (name: string) =>
      !name.includes("__MACOSX/") && isImageFileName(name);

    // Pass-through is only safe for a single fresh zip: merging several zips or
    // mixing loose files would still require building a new archive.
    const isFreshSingleZip =
      listFiles.length === 0 &&
      looseImageFiles.length === 0 &&
      zipFiles.length === 1;
    let detectedPassthroughZip: File | null = null;

    const collected: ImportListFile[] = [];
    let total = looseImageFiles.length;
    let loaded = 0;

    const reportLoadProgress = (nextLoaded: number, nextTotal: number) => {
      loaded = nextLoaded;
      total = nextTotal;
      setLoadProgress({ loaded, total });
    };

    try {
      for (const file of zipFiles) {
        // Cheap conformity gate: read only the central directory (a few tens of
        // KB) to reject non-flat / non-image zips before paying JSZip's full
        // in-memory load + re-zip. Returns null on ZIP64/unparseable archives,
        // in which case we fall back to the full reader below.
        // const zipEntries = await inspectZipEntries(file);
        // if (zipEntries) {
        //   console.log("zipEntries", zipEntries);
        //   const conformity = checkZipConformity(zipEntries);
        //   if (!conformity.conforming) {
        //     setDropError(buildZipConformityMessage(conformity));
        //     continue;
        //   }
        // }

        try {
          const zipData = await loadAsync(file);
          const imageEntries = Object.keys(zipData.files)
            .filter(isImageEntryName)
            .map((key) => zipData.files[key]);

          // The original zip can be uploaded untouched only if every entry is
          // already a flat image (no subfolder, no __MACOSX, no extra files),
          // i.e. identical to what we would rebuild from the extracted files.
          if (isFreshSingleZip) {
            const allEntryNames = Object.keys(zipData.files).filter(
              (name) => !zipData.files[name].dir,
            );
            const isFlatImageOnlyZip =
              allEntryNames.length > 0 &&
              allEntryNames.every(
                (name) => !name.includes("/") && isImageEntryName(name),
              );
            if (isFlatImageOnlyZip) {
              detectedPassthroughZip = file;
            }            
          }

          total += imageEntries.length;
          reportLoadProgress(loaded, total);

          for (let i = 0; i < imageEntries.length; i++) {
            const entry = imageEntries[i];
            const blob = await entry.async("blob");
            collected.push(
              createListFile(
                new File([blob], entry.name.replace(/\//g, "_"), {
                  type: filenameToMimeType(entry.name),
                }),
              ),
            );
            const nextLoaded = loaded + 1;
            if (
              i === 0 ||
              i === imageEntries.length - 1 ||
              nextLoaded % 10 === 0
            ) {
              reportLoadProgress(nextLoaded, total);
            } else {
              loaded = nextLoaded;
            }
            if ((i + 1) % FILE_LOAD_YIELD_EVERY === 0) {
              await yieldToUi();
            }
          }
        } catch (e) {
          console.error("Erreur with zip file, try another file", e);
          setDropError("Erreur avec le fichier zip, essayez un autre fichier");
        }
      }

      for (let i = 0; i < looseImageFiles.length; i++) {
        collected.push(createListFile(looseImageFiles[i]));
        const nextLoaded = loaded + 1;
        if (
          i === 0 ||
          i === looseImageFiles.length - 1 ||
          nextLoaded % 10 === 0
        ) {
          reportLoadProgress(nextLoaded, total);
        } else {
          loaded = nextLoaded;
        }
        if ((i + 1) % FILE_LOAD_YIELD_EVERY === 0) {
          await yieldToUi();
        }
      }

      reportLoadProgress(loaded, total);
      setPassthroughZip(detectedPassthroughZip);
      if (collected.length > 0) {
        setIsValidatingFiles(true);
        setListFiles((prev) => [...prev, ...collected]);
      }
    } finally {
      setIsLoadingFiles(false);
    }
  };

  useEffect(() => {
    setDatas((d: any) => ({ ...d, projectId: project.id }));
  }, [project]);

  useEffect(() => {
    setBigFiles(
      findBigFiles(
        toRawFiles(listFiles),
        uploadProperties?.["max-file-size"] ?? "10MB",
      ) as string[],
    );
    setDuplicateFileNames(findDuplicateFileNames(toRawFiles(listFiles)) as string[]);

    if (listFiles.length === 0) {
      setFilesWithDifferentDimensions([]);
      setIsValidatingFiles(false);
      return;
    }

    let isCancelled = false;
    setIsValidatingFiles(true);
    (async () => {
      try {
        const files = await findFilesWithDifferentDimensions(toRawFiles(listFiles));
        if (!isCancelled) {
          setFilesWithDifferentDimensions(files as string[]);
        }
      } catch (e) {
        if (!isCancelled) {
          setFilesWithDifferentDimensions([]);
        }
      } finally {
        if (!isCancelled) {
          setIsValidatingFiles(false);
        }
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [listFiles, uploadProperties]);

  useEffect(() => {
    if (!activeFilter) {
      return;
    }
    const stillHasMatches = listFiles.some((entry) =>
      matchesFilter(entry.file.name, activeFilter),
    );
    if (!stillHasMatches) {
      setActiveFilter(null);
    }
  }, [listFiles, activeFilter, bigSet, duplicateSet, diffDimSet]);

  useEffect(() => {
    if (!submitting) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [submitting]);

  useEffect(() => {
    if (!previewFile) {
      setPreviewLoading(false);
      const canvas = previewCanvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext("2d");
        ctx?.clearRect(0, 0, canvas.width, canvas.height);
        canvas.width = 0;
        canvas.height = 0;
      }
      return;
    }

    let cancelled = false;
    setPreviewLoading(true);

    (async () => {
      try {
        const bitmap = await createImageBitmapFromFile(previewFile, {
          resizeWidth: PREVIEW_MAX_WIDTH,
        });
        if (cancelled) {
          bitmap.close();
          return;
        }

        const canvas = previewCanvasRef.current;
        if (!canvas) {
          bitmap.close();
          return;
        }

        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          bitmap.close();
          return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(bitmap, 0, 0);
        bitmap.close();
      } catch {
        if (!cancelled) {
          const canvas = previewCanvasRef.current;
          if (canvas) {
            const ctx = canvas.getContext("2d");
            ctx?.clearRect(0, 0, canvas.width, canvas.height);
            canvas.width = 0;
            canvas.height = 0;
          }
        }
      } finally {
        if (!cancelled) {
          setPreviewLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [previewFile]);

  const closePreview = () => {
    setPreviewFile(null);
  };

  const handleChange = (e: any) => {
    if (uploadError) {
      setUploadError("");
    }
    if (progress > 0) {
      setProgress(0);
    }
    setDatas({
      ...datas,
      [e.target.name]: e.target.value,
    });
  };

  const prepareZipBlob = useCallback((): Promise<Blob> => {
    const zipdoc = new JSZIP();
    const usedZipNames = new Set<string>();
    for (const { file } of listFiles) {
      const entryName = RENAME_DUPLICATE_FILES_IN_ZIP
        ? makeUniqueZipName(file.name, usedZipNames)
        : file.name;
      zipdoc.file(entryName, file);
    }

    // Stream the archive instead of building it whole in the JS heap. Each
    // streamed chunk is folded into a disk-backed Blob (browser-managed), so the
    // heap only holds ~ZIP_STREAM_FLUSH_BYTES at a time. This avoids the multi-GB
    // heap spike of generateAsync({type:"blob"}) that crashes low-memory tabs.
    //
    // Images (jpg/png) are already compressed: STORE skips DEFLATE so zipping is
    // near-instant. streamFiles:true reads each input file lazily.
    return new Promise<Blob>((resolve, reject) => {
      const blobParts: Blob[] = [];
      let pending: Uint8Array[] = [];
      let pendingBytes = 0;

      const flushPending = () => {
        if (pending.length === 0) {
          return;
        }
        blobParts.push(new Blob(pending as unknown as BlobPart[]));
        pending = [];
        pendingBytes = 0;
      };

      zipdoc
        .generateInternalStream({
          type: "uint8array",
          compression: "STORE",
          streamFiles: true,
        })
        .on("data", (chunk: Uint8Array) => {
          pending.push(chunk);
          pendingBytes += chunk.length;
          if (pendingBytes >= ZIP_STREAM_FLUSH_BYTES) {
            flushPending();
          }
        })
        .on("error", (error: any) => {
          reject(error);
        })
        .on("end", () => {
          flushPending();
          resolve(new Blob(blobParts, { type: "application/zip" }));
        })
        .resume();
    });
  }, [listFiles]);

  const submit = async (e: any) => {
    e.preventDefault();
    if (disabledReason) {
      return;
    }
    setSubmitting(true);
    setIsPreparingZip(!passthroughZip);
    setUploadError("");
    const { subFolderName } = datas;

    const formData = new FormData();

    try {
      if (passthroughZip) {
        // The dropped zip is already a flat, image-only archive: upload it
        // untouched, skipping both extraction and re-zipping entirely.
        formData.append("filesZip", passthroughZip, "images.zip");
      } else {
        const zipBlob = await prepareZipBlob();
        formData.append("filesZip", zipBlob, "images.zip");
      }
    } catch (zipError) {
      setSubmitting(false);
      setIsPreparingZip(false);
      setUploadError("Erreur lors de la préparation du fichier zip");
      console.error("zip preparation error", zipError);
      return;
    }

    setIsPreparingZip(false);

    uploadFiles({ formData, subFolderName })
      .then(() => {
        setSubmitting(false);
        setIsPreparingZip(false);
        setTimeout(() => history.push("/projectsOperations"), 2000);
      })
      .catch((e: any) => {
        setSubmitting(false);
        setIsPreparingZip(false);
        if (axios.isCancel(e)) {
          return;
        }
        if (progress > 0) {
          setProgress(0);
        }
        setUploadError(
          e?.response?.data?.message ?? "Une erreur est survenue",
        );
        console.error("error", e?.response?.data?.message || e);
      });
  };

  const removeFile = useCallback((uid: number) => {
    setListFiles((prev) => prev.filter((entry) => entry.uid !== uid));
    setPassthroughZip(null);
    setUploadError((current) => (current ? "" : current));
    setDropError((current) => (current ? "" : current));
    setProgress((currentProgress: number) =>
      currentProgress > 0 ? 0 : currentProgress,
    );
  }, [setProgress]);

  const fileListItemData = useMemo<ImportFileListItemData>(
    () => ({
      entries: displayedFiles,
      bigSet,
      duplicateSet,
      diffDimSet,
      activeFilter,
      onRemove: removeFile,
      onPreview: setPreviewFile,
      filePreviewButtonClass: classes.filePreviewButton,
    }),
    [
      displayedFiles,
      bigSet,
      duplicateSet,
      diffDimSet,
      activeFilter,
      removeFile,
      classes.filePreviewButton,
    ],
  );

  const clearAllFiles = () => {
    setListFiles([]);
    nextFileUidRef.current = 0;
    setPassthroughZip(null);
    setActiveFilter(null);
    setDropError("");
    setUploadError("");
    if (progress > 0) {
      setProgress(0);
    }
  };

  const clearErrorFiles = () => {
    setListFiles((prev) =>
      prev.filter((entry) => shouldKeepFileOnErrorCleanup(entry.file)),
    );
    setPassthroughZip(null);
    setActiveFilter(null);
    setDropError("");
    setUploadError("");
    if (progress > 0) {
      setProgress(0);
    }
  };

  const clearErrorFilesByType = (type: IssueType) => {
    setListFiles((prev) =>
      prev.filter((entry) => shouldKeepFileOnTypeCleanup(entry.file, type)),
    );
    setPassthroughZip(null);
    if (activeFilter === type) {
      setActiveFilter(null);
    }
    setDropError("");
    setUploadError("");
    if (progress > 0) {
      setProgress(0);
    }
  };

  const isCompactDropzone = listFiles.length > 0;
  const isDropzoneBusy = isLoadingFiles || isValidatingFiles;
  const dropzoneProgressPercent =
    loadProgress.total > 0
      ? Math.round((loadProgress.loaded / loadProgress.total) * 100)
      : 0;
  const dropzoneLoadingMessage = isLoadingFiles
    ? "Chargement de vos fichiers en cours ..."
    : "Validation des fichiers en cours ...";

  const disabledReason = isLoadingFiles
    ? "Chargement des fichiers en cours"
    : isValidatingFiles
      ? "Validation des fichiers en cours"
      : !datas.subFolderName?.trim()
        ? "Renseignez un dossier de stockage"
        : listFiles.length === 0
          ? "Ajoutez au moins un fichier"
          : moreThanMaxTotalSize
            ? `La taille totale (${humanFileSize(totalSize, 3)}) dépasse la limite (${maxRequestSizeLabel}). Retirez environ ${humanFileSize(totalSizeOverBytes)} de fichiers.`
            : bigFiles.length > 0
              ? "Des fichiers sont trop volumineux"
              : filesWithDifferentDimensions.length > 0
                ? "Des fichiers n'ont pas les mêmes dimensions"
                : "";

  const filesSummary = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        padding: "8px 14px",
        backgroundColor: "#f7f8fa",
        border: "1px solid #eceef1",
        borderRadius: 10,
      }}
    >
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontWeight: 600,
          color: "#3a4654",
        }}
      >
        <Collections style={{ fontSize: 20, color: "#7a8794" }} />
        {listFiles.length} fichier(s)
      </span>
      <span
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          justifyContent: "flex-end",
          gap: 10,
          textAlign: "right",
        }}
      >
        <span
          style={{
            color: "#9aa6b2",
            fontWeight: 400,
            letterSpacing: "0.03em",
          }}
        >
          max {uploadProperties["max-file-size"]} par fichier
        </span>
        <span style={{ color: "#9aa6b2", fontWeight: 400 }}>·</span>
        <Tooltip
          title={
            moreThanMaxTotalSize ? (
              <span style={{ whiteSpace: "pre-line" }}>
                {`Total : ${totalSize.toLocaleString("fr-FR")} octets\nLimite : ${maxRequestBytes.toLocaleString("fr-FR")} octets (${maxRequestSizeLabel})\nDépassement : ${totalSizeOverBytes.toLocaleString("fr-FR")} octets (~${humanFileSize(totalSizeOverBytes)})`}
              </span>
            ) : (
              ""
            )
          }
          disableHoverListener={!moreThanMaxTotalSize}
        >
          <span style={{ fontWeight: 600, color: "#3a4654" }}>
            <span style={{ color: moreThanMaxTotalSize ? "#d32f2f" : "inherit" }}>
              {humanFileSize(totalSize, moreThanMaxTotalSize ? 3 : 2)}
            </span>
            <span style={{ color: "#9aa6b2", fontWeight: 400 }}>
              {" "}
              / {maxRequestSizeLabel}
            </span>
            {moreThanMaxTotalSize && (
              <span style={{ color: "#d32f2f", fontWeight: 600 }}>
                {" "}
                (+{humanFileSize(totalSizeOverBytes)})
              </span>
            )}
          </span>
        </Tooltip>
      </span>
    </div>
  );

  return (
    <Grid
      container
      direction="row"
      justify="center"
      alignItems="flex-start"
      className={classes.root}
    >
      <Helmet>
        <title>Importer des images</title>
      </Helmet>
      <Grid item xs={12} sm={8}>
        <Paper className={classes.paper}>
          <UiFormTitle
            info={
              listFiles.length > 0
                ? [project.name, project.client?.name]
                    .filter(Boolean)
                    .join(" · ") || undefined
                : undefined
            }
          >
            Importer des images
          </UiFormTitle>
          <form onSubmit={submit} id="form" className={classes.formShell}>
            <div className={classes.formBody}>
              <Grid
                container
                spacing={8}
                className={classNames(classes.form, classes.formGrid)}
              >
                {listFiles.length === 0 && (
                  <Grid item xs={12}>
                    <ImportProjectSummary project={project} />
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <UiInput
                    label="Dossier de stockage"
                    autoFocus
                    name="subFolderName"
                    required
                    onChange={handleChange}
                    value={datas.subFolderName ?? ""}
                    tabIndex={2}
                  />
                </Grid>
                {(submitting || progress > 0) && (
                  <Grid item xs={12}>
                    <Grid
                      container
                      className={classNames(classes.dropContainer, {
                        [classes.uploading]: true,
                      })}
                      alignItems="center"
                      justify="center"
                    >
                      <Grid item xs={12} sm={8}>
                        <UiProgress
                          variant={
                            progress > 0 ? "determinate" : "indeterminate"
                          }
                          color="secondary"
                          value={progress}
                        />
                        {submitting && isPreparingZip && progress === 0 ? (
                          <small>Préparation de l&apos;import...</small>
                        ) : submitting && progress === 0 ? (
                          <small>Démarrage de l&apos;envoi...</small>
                        ) : progress > 0 && progress < 100 ? (
                          <small>
                            Import des images en cours... {progress} %
                          </small>
                        ) : isUploading && progress === 100 ? (
                          <>
                            <small>Traitement en cours...</small>
                            <br />
                            <small
                              style={{ fontStyle: "italic", fontSize: "16px" }}
                            >
                              Veuillez ne pas fermer la page
                            </small>
                          </>
                        ) : !isUploading && progress === 100 ? (
                          <small>Traitement fini</small>
                        ) : (
                          <small>Import des images en cours...</small>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                )}

                {uploadError && !submitting && progress === 0 && (
                  <Grid item xs={12}>
                    <Grid
                      container
                      className={classNames(classes.dropContainer, {
                        [classes.uploading]: true,
                      })}
                      alignItems="center"
                      justify="center"
                    >
                      <Grid item xs={12} className={classNames(classes.error)}>
                        {uploadError}
                      </Grid>
                    </Grid>
                  </Grid>
                )}

                {progress === 0 && !submitting && (
                  <Grid item xs={12} style={{ flexShrink: 0 }}>
                    <UiFileDropzone
                      accept={IMPORT_ALLOWED_FILE_TYPES_ACCEPT}
                      multiple
                      variant={isCompactDropzone ? "compact" : "default"}
                      disabled={isDropzoneBusy}
                      error={dropError || null}
                      onDrop={onDrop}
                      dragActiveText="Déposez vos fichiers ici"
                      idlePrefix={
                        isCompactDropzone
                          ? "Glisser-déposer ou"
                          : "Glisser-déposer vos images ou"
                      }
                      chooseButtonLabel={
                        isCompactDropzone
                          ? "ajouter des fichiers"
                          : "choisir des fichiers"
                      }
                      loading={isDropzoneBusy}
                      loadingContent={
                        <Grid
                          container
                          justify="center"
                          style={{ width: "100%" }}
                        >
                          <Grid item xs={12} sm={8}>
                            <p
                              style={{
                                margin: 0,
                                fontSize: isCompactDropzone ? 13 : 16,
                                fontWeight: 400,
                                lineHeight: isCompactDropzone ? 1.3 : 1.6,
                                textAlign: "center",
                              }}
                            >
                              {dropzoneLoadingMessage}
                            </p>
                            <UiProgress
                              variant={
                                isValidatingFiles && !isLoadingFiles
                                  ? "indeterminate"
                                  : "determinate"
                              }
                              color="secondary"
                              compact={isCompactDropzone}
                              value={dropzoneProgressPercent}
                            />
                          </Grid>
                        </Grid>
                      }
                      footer={
                        isCompactDropzone ? undefined : (
                          <>
                            <div>
                              Max {uploadProperties["max-file-size"]} par fichier
                              et au total max{" "}
                              {uploadProperties["max-request-size"]}
                            </div>
                            <div style={{ marginTop: 8 }}>
                              {IMPORT_ALLOWED_FILE_TYPES_LABEL}
                            </div>
                          </>
                        )
                      }
                    />
                  </Grid>
                )}

                {listFiles.length > 0 && (
                  <Grid item xs={12} className={classes.fileListSection}>
                    <div>{filesSummary}</div>

                    {(bigFiles.length > 0 ||
                      duplicateFileNames.length > 0 ||
                      filesWithDifferentDimensions.length > 0) && (
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 8,
                            alignItems: "center",
                            marginTop: 12,
                          }}
                        >
                          {bigFiles.length > 0 && (
                            <ImportIssueChip
                              label={`${countRemovableByType("big")} trop volumineux`}
                              icon={<Storage />}
                              filterKey="big"
                              activeFilter={activeFilter}
                              onActiveFilterChange={setActiveFilter}
                              removeTooltip={`Retirer les fichiers trop volumineux (${countRemovableByType(
                                "big",
                              )})`}
                              onRemove={() => clearErrorFilesByType("big")}
                              color="error"
                              groupClassName={classes.issueChipGroup}
                              removeButtonClassName={classes.issueChipRemoveButton}
                            />
                          )}
                          {duplicateFileNames.length > 0 && (
                            <ImportIssueChip
                              label={`${duplicateFileNames.length} doublon${duplicateFileNames.length > 1 ? "s" : ""}`}
                              icon={<FileCopy />}
                              filterKey="duplicates"
                              activeFilter={activeFilter}
                              onActiveFilterChange={setActiveFilter}
                              removeTooltip={`Retirer tous les doublons (${countRemovableByType(
                                "duplicates",
                              )})`}
                              onRemove={() => clearErrorFilesByType("duplicates")}
                              color="warning"
                              groupClassName={classes.issueChipGroup}
                              removeButtonClassName={classes.issueChipRemoveButton}
                            />
                          )}
                          {filesWithDifferentDimensions.length > 0 && (
                            <ImportIssueChip
                              label={`${countRemovableByType("dimensions")} dim. différentes`}
                              icon={<AspectRatio />}
                              filterKey="dimensions"
                              activeFilter={activeFilter}
                              onActiveFilterChange={setActiveFilter}
                              removeTooltip={`Retirer les fichiers aux dimensions différentes (${countRemovableByType(
                                "dimensions",
                              )})`}
                              onRemove={() => clearErrorFilesByType("dimensions")}
                              color="error"
                              groupClassName={classes.issueChipGroup}
                              removeButtonClassName={classes.issueChipRemoveButton}
                            />
                          )}
                          {activeFilter && (
                            <button
                              type="button"
                              className={classes.browseCrumbLink}
                              onClick={() => setActiveFilter(null)}
                            >
                              Réinitialiser
                            </button>
                          )}
                        </div>
                      )}

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginTop: 16,
                      }}
                    >
                      <span>Liste des fichiers</span>
                      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                        {filesWithIssuesCount > 0 && (
                          <button
                            type="button"
                            className={classes.browseCrumbLink}
                            onClick={clearErrorFiles}
                          >
                            Retirer les fichiers en erreur ({filesWithIssuesCount})
                          </button>
                        )}
                        <button
                          type="button"
                          className={classes.browseCrumbLink}
                          onClick={clearAllFiles}
                        >
                          Tout vider
                        </button>
                      </div>
                    </div>

                    <div
                      ref={fileListScrollRef}
                      className={classes.fileListScroll}
                    >
                      {displayedFiles.length > 0 && fileListSize.width > 0 && (
                        <List
                          height={fileListHeight}
                          width={fileListSize.width}
                          itemCount={displayedFiles.length}
                          itemSize={FILE_LIST_ROW_HEIGHT}
                          itemData={fileListItemData}
                          itemKey={(index, data) =>
                            data.entries[index]?.uid ?? index
                          }
                          outerElementType={FileListOuterElement}
                          className={classes.fileListOuter}
                        >
                          {ImportFileListRow}
                        </List>
                      )}
                    </div>
                  </Grid>
                )}

              </Grid>
            </div>

            <div className={classes.formFooter}>
              <div className={classes.formFooterRow}>
                {(uploadError || dropError) && (
                  <Tooltip title={uploadError || dropError}>
                    <div className={classes.formFooterError}>
                      Erreur{uploadError ? " d'import" : ""} :{" "}
                      {uploadError || dropError}
                    </div>
                  </Tooltip>
                )}
                <div className={classes.formFooterActions}>
                  {submitting && isUploading && Number(progress) < 100 && (
                    <UiButton
                      className={classes.formFooterButtonCancel}
                      color="default"
                      variant="outlined"
                      type="button"
                      onClick={(e: any) => {
                        e.preventDefault();
                        cancelUpload?.();
                      }}
                    >
                      Annuler l&apos;import
                    </UiButton>
                  )}
                  <Tooltip
                    title={disabledReason}
                    disableHoverListener={submitting || !disabledReason}
                  >
                    <span className={classes.formFooterSubmitWrap}>
                      <UiButton
                        className={classes.formFooterButtonSubmit}
                        color="secondary"
                        variant="contained"
                        type="submit"
                        tabIndex={3}
                        disabled={submitting || !!disabledReason}
                      >
                        {submitting
                          ? "Import des images en cours..."
                          : "Importer vos images"}
                      </UiButton>
                    </span>
                  </Tooltip>
                </div>
              </div>
            </div>
          </form>
        </Paper>
        <Dialog
          open={!!previewFile}
          onClose={closePreview}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle disableTypography className={classes.filePreviewDialogTitle}>
            <span className={classes.filePreviewDialogTitleText}>
              {previewFile?.name}
            </span>
            <IconButton
              aria-label="Fermer"
              onClick={closePreview}
              className={classes.filePreviewDialogCloseButton}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {previewLoading && (
              <small>Chargement de l&apos;aperçu...</small>
            )}
            <canvas
              ref={previewCanvasRef}
              className={classes.filePreviewImage}
              aria-label={previewFile?.name}
            />
          </DialogContent>
        </Dialog>
      </Grid>
    </Grid>
  );
};

export default ImportLocalForm;
